import * as React from 'react';
import {SkuDetailsProps} from './SkuDetails';
import {InventoryInfo} from './ItemMasterInterfaces';
import {SkuDetailProperties} from './ItemMasterEdit';
import {InventoryGroups, SerialNumberProgram} from './ItemMasterDetail';

export interface SkuDetailsEditProps extends SkuDetailsProps {
  isShipper: boolean;
  onChangeInfo: (patch: Partial<InventoryInfo>) => void;
  onChangeSkuDetailProperties: (patch: Partial<SkuDetailProperties>) => void;
  serial_number_programs: SerialNumberProgram[];
  inventoryGroups: InventoryGroups[];
  skuDetailProperties: SkuDetailProperties;
}

const UN_OPTIONS = [
  'UN1078',
  'UN3090',
  'UN3091',
  'UN3163',
  'UN3252',
  'UN3480',
  'UN3481',
  'UN3171',
  'UN1057',
  'UN3082',
  'UN1759',
  'UN1133',
  'UN1950',
  'UN1263'
];

const SkuDetailsEdit: React.FC<SkuDetailsEditProps> = (props: SkuDetailsEditProps) => {
  const {onChangeInfo, onChangeSkuDetailProperties, inventoryInfo} = props;

  const [infoState, setInfoState] = React.useState<InventoryInfo>(props.inventoryInfo);
  const [skuDetailPropertiesState, setSkuDetailPropertiesState] = React.useState<SkuDetailProperties>(
    props.skuDetailProperties
  );

  React.useEffect(() => {
    setInfoState(props.inventoryInfo);
  }, [props.inventoryInfo]);
  React.useEffect(() => {
    setSkuDetailPropertiesState(props.skuDetailProperties);
  }, [props.skuDetailProperties]);

  const isLotCodeTrackingEditable = () =>
    props.inventory_configuration && (props.isShipper || !props.inventory_control_user || props.fai_receive_workflow);

  const updateInfo = (patch: Partial<InventoryInfo>) => {
    const next = {...infoState, ...patch} as InventoryInfo;
    setInfoState(next);
    onChangeInfo(patch);
  };

  const updateSkuDetailProperties = (patch: Partial<SkuDetailProperties>) => {
    const next = {...skuDetailPropertiesState, ...patch} as SkuDetailProperties;
    setSkuDetailPropertiesState(next);
    onChangeSkuDetailProperties(patch);
  };

  const updateCsa = (key: string, value: string) => {
    const nextCsa = {...infoState.csa};
    nextCsa[key] = value;
    updateInfo({csa: nextCsa});
    onChangeInfo({csa: nextCsa});
  };

  return (
    <div className="item-master-tile">
      <div className="col">
        <div className="header">
          <b>SKU Details</b>
        </div>

        {props.altIdPresence && (
          <div className="row">
            <label className="col-sm-12 form-label tile-line-item">Alternate ID</label>
            <div className="col-sm-12 tile-line-item">
              <input
                type="text"
                className="form-control input-sm"
                value={infoState.alternate_id || ''}
                onChange={(e) => updateInfo({alternate_id: e.target.value})}
              />
            </div>
          </div>
        )}

        <div className="row">
          <label className="col-sm-12 form-label tile-line-item">HS Code:</label>
          <div className="col-sm-12 tile-line-item">
            <input
              type="text"
              className="form-control input-sm"
              value={infoState.hs_code || ''}
              onChange={(e) => updateInfo({hs_code: e.target.value})}
            />
          </div>
        </div>

        <div className="row">
          <label className="col-sm-12 form-label tile-line-item">Country of Origin:</label>
          <div className="col-sm-12 tile-line-item">
            <input
              type="text"
              className="form-control input-sm"
              value={infoState.country_of_origin || ''}
              onChange={(e) => updateInfo({country_of_origin: e.target.value})}
            />
          </div>
        </div>

        {props.serial_number_program_enabled && (
          <div className="row">
            <label className="col-sm-12 form-label tile-line-item">Serial Number Program</label>
            <div className="col-sm-12 tile-line-item">
              <select
                className="form-control"
                value={infoState.serial_number_program || ''}
                disabled={!props.isShipper}
                onChange={(e) => updateInfo({serial_number_program: e.target.value === 'None' ? '' : e.target.value})}
              >
                <option value={''}>None</option>
                {props.serial_number_programs?.map((program) => (
                  <option value={program.name}>{program.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {infoState?.inventory_group && props.inventoryGroups && (
          <div className="row">
            <label className="col-sm-12 form-label tile-line-item">Inventory Group</label>
            <div className="col-sm-12 tile-line-item">
              <select
                className="form-control"
                value={infoState.inventory_group}
                onChange={(e) => updateInfo({inventory_group: e.target.value})}
              >
                {props.inventoryGroups?.map((group) => (
                  <option value={group.description}>{group.description}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {isLotCodeTrackingEditable() && (
          <div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>
                  <input
                    type="checkbox"
                    style={{marginRight: 6}}
                    checked={skuDetailPropertiesState.lotTrackingEnabled}
                    onChange={(e) => updateSkuDetailProperties({lotTrackingEnabled: e.target.checked})}
                  />
                  Lot Code Tracking
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>
                  <input
                    type="checkbox"
                    style={{marginRight: 6}}
                    checked={skuDetailPropertiesState.expiryDateTrackingEnabled}
                    onChange={(e) => updateSkuDetailProperties({expiryDateTrackingEnabled: e.target.checked})}
                  />
                  Expiration Date Tracking
                </label>
              </div>
            </div>
          </div>
        )}

        {(props.isShipper || props.inventory_control_user) &&
          props.inventory_configuration &&
          props.vas_receive_workflow && (
            <div>
              <div className="row">
                <div className="col-sm-6 tile-line-item">
                  <label style={{fontWeight: 400}}>
                    <input
                      type="checkbox"
                      style={{marginRight: 6}}
                      checked={skuDetailPropertiesState.bagAndTapeRequired}
                      onChange={(e) => updateSkuDetailProperties({bagAndTapeRequired: e.target.checked})}
                    />
                    Bag & Tape
                  </label>
                </div>
              </div>
            </div>
          )}

        {(props.isShipper || !props.inventory_control_user) && props.hazmat_enabled && props.hazmat_viewdata && (
          <div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>
                  <input
                    type="checkbox"
                    style={{marginRight: 6}}
                    checked={skuDetailPropertiesState.isHazmat}
                    onChange={(e) => updateSkuDetailProperties({isHazmat: e.target.checked})}
                  />
                  Hazmat Classification
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>
                  <input
                    type="checkbox"
                    style={{marginRight: 6}}
                    checked={skuDetailPropertiesState.limitedQuantity}
                    onChange={(e) => updateSkuDetailProperties({limitedQuantity: e.target.checked})}
                  />
                  Limited Quantity
                </label>
              </div>
            </div>
            {skuDetailPropertiesState.isHazmat && (
              <div>
                <div className="row">
                  <label className="col-sm-12 form-label tile-line-item">UN Number:</label>
                  <div className="col-sm-12 tile-line-item">
                    <div className="btn-group" role="group" aria-label="UN Number">
                      {UN_OPTIONS.map((code) => (
                        <label
                          key={code}
                          className={
                            'btn btn-default btn-sm' + (skuDetailPropertiesState.unNumber === code ? ' active' : '')
                          }
                          style={{marginRight: 6, marginBottom: 6}}
                          onClick={() => updateSkuDetailProperties({unNumber: code})}
                        >
                          <input
                            type="radio"
                            name={`un-number-${props.inventoryInfo.id || 'inv'}`}
                            value={code}
                            checked={skuDetailPropertiesState.unNumber === code}
                            onChange={() => updateSkuDetailProperties({unNumber: code})}
                            style={{position: 'absolute', opacity: 0, pointerEvents: 'none'}}
                          />
                          {code}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12 form-label tile-line-item">Package Code:</div>
                  <div className="col-sm-12 tile-line-item">
                    <input
                      type="text"
                      className="form-control input-sm"
                      value={skuDetailPropertiesState.pkgCode}
                      onChange={(e) => updateSkuDetailProperties({pkgCode: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {props.isShipper && props.inventory_configuration?.expiry_date_tracking_enabled && (
          <div className="row">
            <label className="col-sm-12 form-label tile-line-item">Shelf Life Threshold:</label>
            <div className="col-sm-12 tile-line-item">
              <input
                type="number"
                step="any"
                className="form-control input-sm"
                value={inventoryInfo.shelf_life_threshold == null ? '' : String(inventoryInfo.shelf_life_threshold)}
                onChange={(e) => updateInfo({shelf_life_threshold: Number(e.target.value) || 0})}
              />
            </div>
          </div>
        )}

        {props.isShipper && props.enable_item_storage_class && (
          <>
            <div className="row">
              <label className="col-sm-12 form-label tile-line-item">Temperature:</label>
              <div className="col-sm-12 tile-line-item">
                <input
                  type="text"
                  className="form-control input-sm"
                  value={inventoryInfo.temperature || ''}
                  onChange={(e) => updateInfo({temperature: e.target.value})}
                />
              </div>
            </div>
          </>
        )}

        {inventoryInfo.csa && (
          <div>
            {Object.keys(inventoryInfo.csa as Record<string, string>).map((key) => (
              <div className="row" key={key}>
                <label className="col-sm-12 form-label tile-line-item">{`CSA-${key}:`}</label>
                <div className="col-sm-12 tile-line-item">
                  <input
                    type="text"
                    className="form-control input-sm"
                    value={(inventoryInfo.csa as Record<string, string>)[key] || ''}
                    onChange={(e) => updateCsa(key, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkuDetailsEdit;
